import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../services/api';  // ✅ Use centralized API client

// Async thunks for API calls

// Get activities for the school
export const fetchActivities = createAsyncThunk(
  'uniform/fetchActivities',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/studentuniform/activities');
      return response.data.data; // ApiResponse<List<ActivityDto>>
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load activities');
    }
  }
);

// Get tahuns for the school
export const fetchTahuns = createAsyncThunk(
  'uniform/fetchTahuns',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/studentuniform/tahuns');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load tahuns');
    }
  }
);

// Get size configuration for an activity
export const fetchSizeConfig = createAsyncThunk(
  'uniform/fetchSizeConfig',
  async (activityId, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/studentuniform/size-config/${activityId}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load size configuration');
    }
  }
);

// Get students with uniform data
export const fetchStudents = createAsyncThunk(
  'uniform/fetchStudents',
  async ({ activityId, tahun }, { rejectWithValue }) => {
    try {
      console.log('🌐 API Call: fetchStudents', { activityId, tahun });
      const response = await apiClient.get('/studentuniform/students', {
        params: { activityId, tahun }
      });
      console.log('📥 API Response: fetchStudents', response.data);
      return response.data.data; // GetStudentsResponse
    } catch (error) {
      console.error('❌ API Error: fetchStudents', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to load students');
    }
  }
);

// Get school information for header
export const fetchSchoolInfo = createAsyncThunk(
  'uniform/fetchSchoolInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/studentuniform/school-info');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load school information');
    }
  }
);

// Get submission status
export const fetchStatus = createAsyncThunk(
  'uniform/fetchStatus',
  async ({ activityId, tahun }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/studentuniform/status', {
        params: { activityId, tahun }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load status');
    }
  }
);

// Save uniform data (draft)
export const saveUniform = createAsyncThunk(
  'uniform/save',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/studentuniform/save', data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save uniform data');
    }
  }
);

// Submit uniform data (final)
export const submitUniform = createAsyncThunk(
  'uniform/submit',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/studentuniform/submit', data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit uniform data');
    }
  }
);

// Initial state
const initialState = {
  // Data
  schoolInfo: null, // School information for header display
  activities: [],
  tahuns: [],
  students: [],
  sizeConfig: null,
  status: null,

  // Selection
  selectedActivity: null,
  selectedTahun: null,

  // Statistics
  totalStudents: 0,
  maleStudents: 0,
  femaleStudents: 0,

  // Edit tracking
  editedStudents: [], // Array of student IDs with unsaved changes
  deletedStudents: [], // Array of student IDs marked for deletion

  // Loading states
  loadingSchoolInfo: false,
  loadingActivities: false,
  loadingTahuns: false,
  loadingStudents: false,
  loadingSizeConfig: false,
  loadingStatus: false,
  saving: false,
  submitting: false,

  // Error states
  error: null,
};

const uniformSlice = createSlice({
  name: 'uniform',
  initialState,
  reducers: {
    // Set selected activity
    setSelectedActivity: (state, action) => {
      state.selectedActivity = action.payload;
      // Clear students when activity changes
      state.students = [];
      state.editedStudents = [];
      state.deletedStudents = [];
    },

    // Set selected tahun
    setSelectedTahun: (state, action) => {
      state.selectedTahun = action.payload;
      // Clear students when tahun changes
      state.students = [];
      state.editedStudents = [];
      state.deletedStudents = [];
    },

    // Update a student's uniform data
    updateStudent: (state, action) => {
      const { studentId, field, value } = action.payload;
      const studentIndex = state.students.findIndex(s => s.studentId === studentId);

      if (studentIndex !== -1) {
        state.students[studentIndex][field] = value;
        state.students[studentIndex].isEdited = true;
        if (!state.editedStudents.includes(studentId)) {
          state.editedStudents.push(studentId);
        }
      }
    },

    // Mark student for deletion
    deleteStudent: (state, action) => {
      const { studentId, reason } = action.payload;
      const studentIndex = state.students.findIndex(s => s.studentId === studentId);

      if (studentIndex !== -1) {
        state.students[studentIndex].isDeleted = true;
        state.students[studentIndex].deletedReason = reason;
        if (!state.deletedStudents.includes(studentId)) {
          state.deletedStudents.push(studentId);
        }
        if (!state.editedStudents.includes(studentId)) {
          state.editedStudents.push(studentId);
        }
      }
    },

    // Restore deleted student
    restoreStudent: (state, action) => {
      const studentId = action.payload;
      const studentIndex = state.students.findIndex(s => s.studentId === studentId);

      if (studentIndex !== -1) {
        state.students[studentIndex].isDeleted = false;
        state.students[studentIndex].deletedReason = null;
        state.deletedStudents = state.deletedStudents.filter(id => id !== studentId);
      }
    },

    // Add new student (manually added)
    addStudent: (state, action) => {
      const newStudent = {
        ...action.payload,
        isNewStudent: true,
        isEdited: true,
      };
      state.students.push(newStudent);
      if (!state.editedStudents.includes(newStudent.studentId)) {
        state.editedStudents.push(newStudent.studentId);
      }
    },

    // Clear all edits (after save/submit)
    clearEdits: (state) => {
      state.students.forEach(student => {
        student.isEdited = false;
      });
      state.editedStudents = [];
    },

    // Clear errors
    clearError: (state) => {
      state.error = null;
    },

    // Import validated students (bulk add/update from import)
    importValidatedStudents: (state, action) => {
      const validatedStudents = action.payload;
      console.log('🔄 importValidatedStudents: Processing', validatedStudents.length, 'students');

      validatedStudents.forEach((importedStudent) => {
        if (importedStudent.importAction === 'ADD') {
          // Add new student
          const newStudent = {
            studentId: importedStudent.studentId || 0, // Use provided ID if exists, otherwise 0 for new students
            noKP: importedStudent.noKP,
            name: importedStudent.name,
            citizenStatus: importedStudent.citizenStatus || 'WARGANEGARA',
            jantina: importedStudent.jantina,
            tahun: importedStudent.tahun || '',
            class: importedStudent.class || '',
            bajuPutih: importedStudent.bajuPutih || '',
            seluarPanjang: importedStudent.seluarPanjang || '',
            seluarPendek: importedStudent.seluarPendek || '',
            bajuKurung: importedStudent.bajuKurung || '',
            kainKurung: importedStudent.kainKurung || '',
            pinafore: importedStudent.pinafore || '',
            kasut: importedStudent.kasut || '',
            isDeleted: false,
            deletedReason: null,
            isNewStudent: importedStudent.isNewStudent !== undefined ? importedStudent.isNewStudent : true,
            isEdited: true
          };
          state.students.push(newStudent);
          console.log('✅ Added student:', newStudent.name, 'ID:', newStudent.studentId);

          // Add to editedStudents array to enable Save button
          if (!state.editedStudents.includes(newStudent.studentId)) {
            state.editedStudents.push(newStudent.studentId);
            console.log('📝 Added to editedStudents:', newStudent.studentId, 'Total edited:', state.editedStudents.length);
          }
        } else if (importedStudent.importAction === 'UPDATE') {
          // Update existing student (match by IC)
          const existingIndex = state.students.findIndex(s => s.noKP === importedStudent.noKP);
          if (existingIndex !== -1) {
            // Only update editable fields
            state.students[existingIndex] = {
              ...state.students[existingIndex],
              tahun: importedStudent.tahun || '',
              class: importedStudent.class || '',
              bajuPutih: importedStudent.bajuPutih || '',
              seluarPanjang: importedStudent.seluarPanjang || '',
              seluarPendek: importedStudent.seluarPendek || '',
              bajuKurung: importedStudent.bajuKurung || '',
              kainKurung: importedStudent.kainKurung || '',
              pinafore: importedStudent.pinafore || '',
              kasut: importedStudent.kasut || '',
              isEdited: true
            };
            if (!state.editedStudents.includes(state.students[existingIndex].studentId)) {
              state.editedStudents.push(state.students[existingIndex].studentId);
            }
          }
        }
      });

      // Update student counts after import
      state.totalStudents = state.students.length;
      state.maleStudents = state.students.filter(s => s.jantina === 'L').length;
      state.femaleStudents = state.students.filter(s => s.jantina === 'P').length;

      console.log('📊 Updated counts - Total:', state.totalStudents, 'Male:', state.maleStudents, 'Female:', state.femaleStudents);
      console.log('💾 Edited students:', state.editedStudents.length, 'IDs:', state.editedStudents);
    },

    // Reset state (when navigating away)
    resetUniformState: (state) => {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    // Fetch activities
    builder
      .addCase(fetchActivities.pending, (state) => {
        state.loadingActivities = true;
        state.error = null;
      })
      .addCase(fetchActivities.fulfilled, (state, action) => {
        state.loadingActivities = false;
        state.activities = action.payload;
      })
      .addCase(fetchActivities.rejected, (state, action) => {
        state.loadingActivities = false;
        state.error = action.payload;
      });

    // Fetch school info
    builder
      .addCase(fetchSchoolInfo.pending, (state) => {
        state.loadingSchoolInfo = true;
        state.error = null;
      })
      .addCase(fetchSchoolInfo.fulfilled, (state, action) => {
        state.loadingSchoolInfo = false;
        state.schoolInfo = action.payload;
      })
      .addCase(fetchSchoolInfo.rejected, (state, action) => {
        state.loadingSchoolInfo = false;
        state.error = action.payload;
      });

    // Fetch tahuns
    builder
      .addCase(fetchTahuns.pending, (state) => {
        state.loadingTahuns = true;
        state.error = null;
      })
      .addCase(fetchTahuns.fulfilled, (state, action) => {
        state.loadingTahuns = false;
        state.tahuns = action.payload;
      })
      .addCase(fetchTahuns.rejected, (state, action) => {
        state.loadingTahuns = false;
        state.error = action.payload;
      });

    // Fetch size config
    builder
      .addCase(fetchSizeConfig.pending, (state) => {
        state.loadingSizeConfig = true;
        state.error = null;
      })
      .addCase(fetchSizeConfig.fulfilled, (state, action) => {
        state.loadingSizeConfig = false;
        state.sizeConfig = action.payload;
      })
      .addCase(fetchSizeConfig.rejected, (state, action) => {
        state.loadingSizeConfig = false;
        state.error = action.payload;
      });

    // Fetch students
    builder
      .addCase(fetchStudents.pending, (state) => {
        console.log('📡 Redux: fetchStudents.pending');
        state.loadingStudents = true;
        state.error = null;
      })
      .addCase(fetchStudents.fulfilled, (state, action) => {
        console.log('✅ Redux: fetchStudents.fulfilled', action.payload);
        state.loadingStudents = false;
        state.students = action.payload.students;
        state.sizeConfig = action.payload.sizeConfig;
        state.status = action.payload.status;
        state.totalStudents = action.payload.totalStudents;
        state.maleStudents = action.payload.maleStudents;
        state.femaleStudents = action.payload.femaleStudents;
        state.editedStudents = [];
        state.deletedStudents = [];
        console.log('📊 Redux: Students stored', {
          studentsCount: state.students.length,
          totalStudents: state.totalStudents
        });
      })
      .addCase(fetchStudents.rejected, (state, action) => {
        console.error('❌ Redux: fetchStudents.rejected', action.payload);
        state.loadingStudents = false;
        state.error = action.payload;
      });

    // Fetch status
    builder
      .addCase(fetchStatus.pending, (state) => {
        state.loadingStatus = true;
        state.error = null;
      })
      .addCase(fetchStatus.fulfilled, (state, action) => {
        state.loadingStatus = false;
        state.status = action.payload;
      })
      .addCase(fetchStatus.rejected, (state, action) => {
        state.loadingStatus = false;
        state.error = action.payload;
      });

    // Save uniform
    builder
      .addCase(saveUniform.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveUniform.fulfilled, (state, action) => {
        state.saving = false;

        // ✅ POST-SAVE CLEANUP (matching old WebForms logic)
        console.log('🧹 Post-Save Cleanup: Starting...');

        // 1. Remove deleted students from the array permanently
        const deletedCount = state.students.filter(s => s.isDeleted).length;
        if (deletedCount > 0) {
          console.log(`🗑️ Removing ${deletedCount} deleted students from state...`);
          state.students = state.students.filter(s => !s.isDeleted);

          // Update student counts after deletion
          state.totalStudents = state.students.length;
          state.maleStudents = state.students.filter(s => s.jantina === 'L').length;
          state.femaleStudents = state.students.filter(s => s.jantina === 'P').length;
        }

        // 2. Clear edit flags on all remaining students
        state.students.forEach(student => {
          student.isEdited = false;
        });

        // 3. Clear tracking arrays
        state.editedStudents = [];
        state.deletedStudents = [];

        console.log('✅ Post-Save Cleanup: Complete');
      })
      .addCase(saveUniform.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });

    // Submit uniform
    builder
      .addCase(submitUniform.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(submitUniform.fulfilled, (state, action) => {
        state.submitting = false;

        // ✅ POST-SUBMIT CLEANUP (same as save, plus status update)
        console.log('🧹 Post-Submit Cleanup: Starting...');

        // 1. Remove deleted students from the array permanently
        const deletedCount = state.students.filter(s => s.isDeleted).length;
        if (deletedCount > 0) {
          console.log(`🗑️ Removing ${deletedCount} deleted students from state...`);
          state.students = state.students.filter(s => !s.isDeleted);

          // Update student counts after deletion
          state.totalStudents = state.students.length;
          state.maleStudents = state.students.filter(s => s.jantina === 'L').length;
          state.femaleStudents = state.students.filter(s => s.jantina === 'P').length;
        }

        // 2. Clear edit flags on all remaining students
        state.students.forEach(student => {
          student.isEdited = false;
        });

        // 3. Clear tracking arrays
        state.editedStudents = [];
        state.deletedStudents = [];

        // 4. Update status to CLOSED (locks editing)
        if (state.status) {
          state.status.status = 'CLOSED';
          state.status.canEdit = false;
          state.status.canSubmit = false;
        }

        console.log('✅ Post-Submit Cleanup: Complete');
      })
      .addCase(submitUniform.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedActivity,
  setSelectedTahun,
  updateStudent,
  deleteStudent,
  restoreStudent,
  addStudent,
  clearEdits,
  clearError,
  importValidatedStudents,
  resetUniformState,
} = uniformSlice.actions;

// Selectors
export const selectSchoolInfo = (state) => state.uniform.schoolInfo;
export const selectActivities = (state) => state.uniform.activities;
export const selectTahuns = (state) => state.uniform.tahuns;
export const selectStudents = (state) => state.uniform.students;
export const selectSizeConfig = (state) => state.uniform.sizeConfig;
export const selectStatus = (state) => state.uniform.status;
export const selectSelectedActivity = (state) => state.uniform.selectedActivity;
export const selectSelectedTahun = (state) => state.uniform.selectedTahun;
export const selectTotalStudents = (state) => state.uniform.totalStudents;
export const selectMaleStudents = (state) => state.uniform.maleStudents;
export const selectFemaleStudents = (state) => state.uniform.femaleStudents;
export const selectHasUnsavedChanges = (state) => state.uniform.editedStudents.length > 0;
export const selectIsLoading = (state) =>
  state.uniform.loadingActivities ||
  state.uniform.loadingTahuns ||
  state.uniform.loadingStudents ||
  state.uniform.saving ||
  state.uniform.submitting;

export default uniformSlice.reducer;
